import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import generalHealtQuestions from '../../data/generalHealtQuestions';
import questionnaireScopeApi from '../../api/questionnaireScopeApi';
import DropdownQuestion from "../../components/questions/dropdownQuestion/dropdownQuestion";
import BaiMatrixQuestion from '../../components/questions/baiMatrixQuestion/baiMatrixQuestion';
import TextQuestion from '../../components/questions/textQuestion/textQuestion';
import HourQuestion from '../../components/questions/hourQuestion/hourQuestion';
import NumericQuestion from '../../components/questions/numericQuestion/numericQuestion';
import responsesApi from '../../api/responsesApi';

import './Nom035Questionnaire.css';
import './generalHealtQuestionarie.css';

const PAGE_SIZE = 5;
const GENERAL_FIELDS = new Set(['email', 'biologicalSex', 'ageRange', 'jobArea']);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HOUR_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
const INTEGER_REGEX = /^[0-9]+$/;

const SECTION_COPY = {
  bdi: {
    title: 'Inventario de Depresión de Beck',
    text: 'Instrucciones: Este cuestionario consta de 21 grupos de afirmaciones. Por favor, lea con atención cada uno de ellos cuidadosamente. Luego elija uno de cada grupo, el que mejor describa el modo como se ha sentido las últimas dos semanas, incluyendo el día de hoy. Marque con un círculo el número correspondiente al enunciado elegido. Si varios enunciados de un mismo grupo le parecen igualmente apropiados, marque el número más alto. Verifique que no haya elegido más de uno por grupo, incluyendo el ítem 16 (cambios en los hábitos de sueño) y el ítem 18 (cambios en el apetito).'
  },
  pss: {
    title: 'Escala de Estrés Percibido',
    text: 'Las preguntas en esta escala hacen referencia a sus sentimientos y pensamientos durante el último mes. En cada caso, por favor indique marcando la opción adecuada cómo usted se ha sentido o ha pensado en cada situación.'
  },
  bai: {
    title: 'Inventario de Ansiedad de Beck',
    text: 'En el cuestionario hay una lista de síntomas comunes de la ansiedad. Lea cada uno de los ítems atentamente, e indique cuánto le ha afectado en la última semana incluyendo hoy:'
  },
  psqi: {
    title: 'Índice de Calidad de Sueño de Pittsburgh',
    text: 'Las siguientes preguntas hacen referencia a la manera en que ha dormido durante el último mes. Intente responder de la manera más exacta posible lo ocurrido durante la mayor parte de los días y noches del último mes. Por favor conteste TODAS las preguntas.'
  }
};

const getQuestionSection = (field) => {
  if (field.startsWith('bdi_item')) return 'bdi';
  if (field.startsWith('pss_item')) return 'pss';
  if (field.startsWith('bai_item')) return 'bai';
  if (field.startsWith('psqi_')) return 'psqi';
  if (GENERAL_FIELDS.has(field)) return 'general';
  return 'general';
};

const isAnswerPresent = (question, value) => {
  if (question.required === false) return true;
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0 && value[0] !== '';
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const getQuestionError = (question, value) => {
  if (!isAnswerPresent(question, value)) return 'Pregunta obligatoria';

  if (question.Component === TextQuestion && question.type === 'email') {
    const email = String(value || '').trim();
    return EMAIL_REGEX.test(email) ? '' : 'Ingresa un correo electrónico válido';
  }

  if (question.Component === HourQuestion && question.required !== false) {
    const hour = String(value || '').trim();
    return HOUR_REGEX.test(hour) ? '' : 'Formato invalido. Usa HH:mm.';
  }

  if (question.Component === NumericQuestion && question.required !== false) {
    const numeric = String(value || '').trim();
    return INTEGER_REGEX.test(numeric) ? '' : 'Ingresa un número válido';
  }

  return '';
};

export default function GeneralHealtQuestionarie() {
  const { idEmpresa, questionnaire, applicationId } = useParams();
  const navigate = useNavigate();

  const questionsRef = useRef([]);
  const containerRef = useRef(null);
  const [answers, setAnswers] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const jobAreaQuestion = useMemo(() => ({
    Component: DropdownQuestion,
    question: "¿Cuál es tu área de trabajo?",
    options: companyJobAreas,
    field: "jobArea",
    required: true
  }), [companyJobAreas]);
  const mergedQuestions = useMemo(() => {
    const arr = [...generalHealtQuestions];
    arr.splice(3, 0, jobAreaQuestion);
    return arr;
  }, [jobAreaQuestion]);
  const questionsWithSection = useMemo(
    () => mergedQuestions.map((q) => ({ ...q, section: getQuestionSection(q.field) })),
    [mergedQuestions]
  );
  const [, setAnsweredState] = useState(
    () => questionsWithSection.map((q) => !q.required)
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [pendingFocusField, setPendingFocusField] = useState(null);

  useEffect(() => {
    questionsRef.current = questionsRef.current.slice(0, questionsWithSection.length);
  }, [questionsWithSection.length]);

  useEffect(() => {
    (async () => {
      try {
        const scope = await questionnaireScopeApi.getById(idEmpresa);
        setCompanyName(scope.name);
        setCompanyJobAreas(scope.jobAreas || []);
      } catch (err) {
        console.error('Error cargando empresa/sucursal:', err);
      }
    })();
  }, [idEmpresa]);

  const markQuestionAsAnswered = useCallback((index, field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value ?? '' }));
    setAnsweredState((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);

  const fieldToIndex = useMemo(
    () => Object.fromEntries(questionsWithSection.map((q, idx) => [q.field, idx])),
    [questionsWithSection]
  );

  const sectionPages = useMemo(() => {
    const pages = [];
    let sectionStart = 0;

    while (sectionStart < questionsWithSection.length) {
      const currentSectionName = questionsWithSection[sectionStart].section;
      let sectionEnd = sectionStart;

      while (
        sectionEnd < questionsWithSection.length &&
        questionsWithSection[sectionEnd].section === currentSectionName
      ) {
        sectionEnd += 1;
      }

      const sectionQuestions = questionsWithSection.slice(sectionStart, sectionEnd);
      for (let i = 0; i < sectionQuestions.length; i += PAGE_SIZE) {
        pages.push({
          section: currentSectionName,
          questions: sectionQuestions.slice(i, i + PAGE_SIZE)
        });
      }

      sectionStart = sectionEnd;
    }

    return pages;
  }, [questionsWithSection]);

  const totalPages = sectionPages.length;
  const currentPageData = sectionPages[currentPage] || { section: 'general', questions: [] };
  const currentSection = currentPageData.section;
  const pageQuestions = currentPageData.questions;
  const progressPct = Math.round(((currentPage + 1) / totalPages) * 100);

  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(totalPages - 1, 0));
    }
  }, [currentPage, totalPages]);

  const focusQuestionByField = useCallback((field) => {
    const idx = fieldToIndex[field];
    if (idx === undefined) return false;

    const questionCard = questionsRef.current[idx];
    if (!questionCard) return false;

    questionCard.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const directFieldInput = questionCard.querySelector(`input[name="${field}"], select[name="${field}"], textarea[name="${field}"]`);
    const firstFocusable = questionCard.querySelector(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    );

    const focusTarget = directFieldInput || firstFocusable || questionCard;
    requestAnimationFrame(() => {
      focusTarget.focus?.({ preventScroll: true });
    });

    return true;
  }, [fieldToIndex]);

  const getFirstInvalidQuestion = useCallback((questionsToCheck) => {
    return questionsToCheck.find((q) => Boolean(getQuestionError(q, answers[q.field])));
  }, [answers]);

  const getPageIndexByField = useCallback((field) => {
    return sectionPages.findIndex((page) => page.questions.some((q) => q.field === field));
  }, [sectionPages]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    if (!pendingFocusField) return;
    if (focusQuestionByField(pendingFocusField)) {
      setPendingFocusField(null);
    }
  }, [pendingFocusField, currentPage, focusQuestionByField]);

  const handleNext = () => {
    const firstInvalid = getFirstInvalidQuestion(pageQuestions);
    if (firstInvalid) {
      focusQuestionByField(firstInvalid.field);
      return;
    }

    setCurrentPage((p) => Math.min(p + 1, totalPages - 1));
  };

  const handlePrev = () => {
    setCurrentPage((p) => Math.max(p - 1, 0));
  };

  const handleSend = async () => {
    const firstInvalid = getFirstInvalidQuestion(questionsWithSection);
    if (firstInvalid) {
      const targetPage = getPageIndexByField(firstInvalid.field);
      if (targetPage >= 0 && targetPage !== currentPage) {
        setPendingFocusField(firstInvalid.field);
        setCurrentPage(targetPage);
      } else {
        focusQuestionByField(firstInvalid.field);
      }
      return;
    }

    const groupedAnswers = questionsWithSection.reduce(
      (acc, q) => {
        const item = {
          field: q.field,
          value: answers[q.field] ?? null
        };

        if (q.field.startsWith('bai_item')) {
          acc.bai.push(item);
        } else if (q.field.startsWith('bdi_item')) {
          acc.bdi.push(item);
        } else if (q.field.startsWith('psqi_')) {
          acc.psqi.push(item);
        } else if (q.field.startsWith('pss_')) {
          acc.pss.push(item);
        } else {
          acc.generalCuestions.push(item);
        }

        return acc;
      },
      { generalCuestions: [], bai: [], bdi: [], psqi: [], pss: [] }
    );

    const timestamp = new Date().toISOString();
    const payload = {
      companyId: idEmpresa,
      questionnaireName: questionnaire,
      answers: groupedAnswers,
      timestamp,
      applicationId: applicationId || null,
    };

    try {
      const result = await responsesApi.createGhqResponse(payload);
      navigate('/cgs/results', { state: { response: result } });
    } catch (error) {
      const serverMsg = error.response?.data?.message;
      alert(serverMsg || 'Error enviando los datos. Intenta de nuevo más tarde.');
      console.error('Error enviando GHQ:', error);
    }
  };

  return (
    <div ref={containerRef} className="questionnaire-container general-health-questionnaire">
      <h2>Evaluación Integral de Bienestar Emocional</h2>
      <p>Empresa: {companyName}</p>
      <p>Lea cuidadosamente cada enunciado y marque la respuesta correspondiente.</p>

      {SECTION_COPY[currentSection] && (
        <div className="question-card" style={{ marginBottom: '1rem' }}>
          <h3>{SECTION_COPY[currentSection].title}</h3>
          <p>{SECTION_COPY[currentSection].text}</p>
        </div>
      )}

      <div className="progress-fill" style={{ width: `${progressPct}%` }} />
      <p className="progress-text">
        Pagina {currentPage + 1} de {totalPages}
      </p>

      {currentSection === 'bai' ? (
        <div
          className="question-card"
          tabIndex={-1}
          ref={(el) => {
            pageQuestions.forEach((question) => {
              questionsRef.current[fieldToIndex[question.field]] = el;
            });
          }}
        >
          <BaiMatrixQuestion
            questions={pageQuestions}
            values={answers}
            onAnswer={(field, value) => markQuestionAsAnswered(fieldToIndex[field], field, value)}
          />
        </div>
      ) : (
        pageQuestions.map((q) => {
          const globalIdx = fieldToIndex[q.field];
          const QuestionComp = q.Component;

          return (
            <div
              key={q.field}
              ref={(el) => {
                questionsRef.current[globalIdx] = el;
              }}
              className="question-card"
              tabIndex={-1}
            >
              <QuestionComp
                question={q.question}
                required={q.required}
                type={q.type}
                options={q.options}
                optionA={q.optionA}
                optionB={q.optionB}
                followUp={q.followUp}
                maxRating={q.maxRating}
                hourRange={q.hourRange}
                invertOrder={q.invertOrder}
                labels={q.labels}
                uniqueChoice={q.uniqueChoice}
                field={q.field}
                value={answers[q.field] ?? ''}
                updateProgress={(val) => markQuestionAsAnswered(globalIdx, q.field, val)}
              />
            </div>
          );
        })
      )}

      <div className="pagination">
        {currentPage > 0 && (
          <button onClick={handlePrev} className="page-button">
            Anterior
          </button>
        )}
        {currentPage < totalPages - 1 ? (
          <button onClick={handleNext} className="page-button">
            Siguiente
          </button>
        ) : (
          <button onClick={handleSend} className="page-button submit-button">
            Enviar
          </button>
        )}
      </div>
    </div>
  );
}
